import { memo } from 'react';
import { useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import useStaticPage from '../queries/useStaticPage';
import { FluidProgressIndicator } from '../../core/components/ProgressIndicators';
import Container from '../../core/components/Container';
import type { BreadcrumbLink } from '../../core/components/LinkBreadcrumbs';
import LinkBreadcrumbs from '../../core/components/LinkBreadcrumbs';
import PageNotFound from '../../core/components/router/PageNotFound';
import type { PageComponent } from '../../content/types/types';
import withErrorBoundary from '../../core/components/errors/withErrorBoundary';
import styles from './StaticPage.module.scss';
import StaticPageContent from './StaticPageContent';

const StaticContent = memo(
    ({ data, loading }: { data?: PageComponent; loading?: boolean }) => {
        if (loading) {
            return (
                <div className={styles.progressIndicatorContainer}>
                    <FluidProgressIndicator />
                </div>
            );
        }

        if (!data) {
            return <PageNotFound />;
        }

        return <StaticPageContent page={data} />;
    }
);

const StaticPage = ({
    prefix = '',
    breadcrumbs = [],
}: {
    prefix?: string;
    breadcrumbs?: BreadcrumbLink[];
}) => {
    const { '*': page = '' } = useParams<{
        '*': string;
    }>();

    const { data, loading } = useStaticPage(prefix + page);

    const pageBreadcrumbs =
        data &&
        breadcrumbs.concat([
            {
                label: data.short_title || data.title,
                isRelative: true,
                to: page,
            },
        ]);

    const Content = data?.full_screen ? 'div' : Container;

    return (
        <div>
            {pageBreadcrumbs && (
                <LinkBreadcrumbs
                    title={data.meta_fields?.title || data.title}
                    description={data.meta_fields?.description}
                    breadcrumbs={pageBreadcrumbs}
                />
            )}
            <div
                className={clsx(styles.container, {
                    [styles.fullScreen]: data?.full_screen,
                })}
                data-test-class={page}
            >
                <Content>
                    <StaticContent data={data} loading={loading} />
                </Content>
            </div>
        </div>
    );
};

export default memo(withErrorBoundary(StaticPage));
